import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { RichTextEditor } from "@/components/settings/RichTextEditor";
import { stripHtml } from "@/lib/emailComposer";
import {
  getAutoGeneratedLeadTemplate,
  LEAD_NOTIFY_PLACEHOLDER_HINTS,
  previewLeadNotificationTemplate,
} from "@/lib/leadNotificationTemplates";

export function LeadNotificationTemplateModal({
  open,
  onOpenChange,
  initialTemplate = "",
  onSaved,
  canEdit = true,
}) {
  const [editorHtml, setEditorHtml] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editorResetKey, setEditorResetKey] = useState(0);
  const autoIndexRef = useRef(0);

  useEffect(() => {
    if (open) {
      setEditorHtml(initialTemplate || "");
      setPreviewOpen(false);
      autoIndexRef.current = 0;
      setEditorResetKey((k) => k + 1);
    }
  }, [open, initialTemplate]);

  const onAutoGenerate = () => {
    const template = getAutoGeneratedLeadTemplate(autoIndexRef.current);
    autoIndexRef.current = (autoIndexRef.current + 1) % 10;
    setEditorHtml(template);
    setEditorResetKey((k) => k + 1);
    setPreviewOpen(false);
    toast.message(`Template #${autoIndexRef.current === 0 ? 10 : autoIndexRef.current} inserted`);
  };

  const onSave = async () => {
    const trimmed = (editorHtml || "").trim();
    if (!trimmed || !stripHtml(trimmed)) {
      toast.error("Enter an email template before saving");
      return;
    }
    setSaving(true);
    try {
      const { generalSettingsApi, apiErrorMessage } = await import("@/lib/api");
      const updated = await generalSettingsApi.update({
        lead_notification_email_template: trimmed,
      });
      const saved = updated?.lead_notification_email_template || trimmed;
      onSaved?.(saved);
      toast.success("Lead notification template saved");
      try {
        window.dispatchEvent(new Event("sellyx:lead-notification-updated"));
      } catch { /* ignore */ }
      onOpenChange(false);
    } catch (e) {
      let msg = e?.message || "Failed to save template";
      try {
        const { apiErrorMessage } = await import("@/lib/api");
        msg = apiErrorMessage(e, msg);
      } catch {
        /* keep msg */
      }
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const onCancel = () => {
    onOpenChange(false);
  };

  const previewHtml = previewLeadNotificationTemplate(editorHtml);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="w-[calc(100vw-1.5rem)] max-w-3xl max-h-[90dvh] overflow-y-auto"
        data-testid="lead-notification-template-modal"
      >
        <DialogHeader>
          <DialogTitle>Lead Notification Email Template</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-[12px] text-slate-600">
            Use placeholders such as {"{{contact_name}}"} and {"{{opportunity_name}}"}.
            They are replaced automatically when a lead is marked Qualified.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {LEAD_NOTIFY_PLACEHOLDER_HINTS.map((token) => (
              <span
                key={token}
                className="rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-mono text-slate-600"
              >
                {token}
              </span>
            ))}
          </div>

          {!previewOpen ? (
            <RichTextEditor
              value={editorHtml}
              onChange={setEditorHtml}
              resetKey={editorResetKey}
              disabled={!canEdit || saving}
              editorTestId="lead-notification-template-editor"
              minHeight={220}
            />
          ) : (
            <div
              className="rounded-md border border-slate-200 bg-white p-4 text-[13px] leading-relaxed text-slate-800 min-h-[220px] [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_a]:text-indigo-600 [&_a]:underline"
              data-testid="lead-notification-template-preview"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: previewHtml }}
            />
          )}

          <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2 pt-1">
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={!canEdit || saving}
                onClick={onAutoGenerate}
                data-testid="lead-notification-auto-generate"
              >
                Auto Generate
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={saving}
                onClick={() => setPreviewOpen((v) => !v)}
                data-testid="lead-notification-preview-toggle"
              >
                {previewOpen ? "Edit" : "Preview"}
              </Button>
            </div>
            <div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={saving}
                onClick={onCancel}
                data-testid="lead-notification-template-cancel"
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={!canEdit || saving}
                onClick={onSave}
                data-testid="lead-notification-template-save"
              >
                {saving ? "Saving…" : "Save"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

LeadNotificationTemplateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  initialTemplate: PropTypes.string,
  onSaved: PropTypes.func,
  canEdit: PropTypes.bool,
};
